import { z } from 'zod';

/**
 * Prénoms et noms en casse « Prénom Nom » (décision du 09/10/2026) :
 * « valentin pompigne » et « Valentin POMPIGNE » deviennent « Valentin
 * Pompigne ». Appliqué à l'ENTRÉE (schémas de création / modification des
 * fiches, comptes et invitations, webhook Clerk) et rejoué sur l'existant
 * par une reprise — la base ne garde qu'une forme.
 *
 * Règle, volontairement simple et prévisible :
 *  - espaces de bord retirés, espaces multiples réduits à un ;
 *  - chaque mot, chaque partie d'un nom composé (tiret) et ce qui suit une
 *    apostrophe prend une majuscule initiale, le reste en minuscules :
 *    « JEAN-PIERRE » → « Jean-Pierre », « o'neil » → « O'Neil » ;
 *  - les particules usuelles restent en minuscules quand elles ne sont pas
 *    en tête : « gil DA COSTA » → « Gil da Costa », « jeanne D'ARC » →
 *    « Jeanne d'Arc » ; en tête, elles prennent la majuscule (« Da Silva »).
 *    « Le » / « La » n'en font pas partie (Le Gall, La Fontaine) ;
 *  - un mot qui contient un chiffre est laissé tel quel (codes, matricules).
 */

/** Particules gardées en minuscules hors du premier mot. */
const PARTICLES = new Set([
  'de',
  'du',
  'des',
  'da',
  'das',
  'do',
  'dos',
  'di',
  'del',
  'della',
  'van',
  'von',
  'der',
  'den',
  'ten',
  'ter',
  'y',
]);

/** Élisions gardées en minuscules hors du premier mot : « d'Arc ». */
const ELISIONS = new Set(["d'", "l'"]);

const APOSTROPHES = /['’]/g;

function capitalize(part: string): string {
  if (!part) return part;
  const lower = part.toLocaleLowerCase('fr-FR');
  return lower.charAt(0).toLocaleUpperCase('fr-FR') + lower.slice(1);
}

/** Majuscule à chaque partie d'un mot : tirets et apostrophes compris. */
function capitalizeWord(word: string): string {
  return word
    .split('-')
    .map((hyphenPart) =>
      hyphenPart
        .split("'")
        .map((p) => capitalize(p))
        .join("'"),
    )
    .join('-');
}

export function normalizePersonName(raw: string): string {
  const words = raw.replace(APOSTROPHES, "'").trim().split(/\s+/).filter(Boolean);
  return words
    .map((word, i) => {
      if (/\d/.test(word)) return word;
      const lower = word.toLocaleLowerCase('fr-FR');
      if (i > 0 && PARTICLES.has(lower)) return lower;
      if (i > 0) {
        // « d'arc » → « d'Arc » : élision en minuscules, la suite en majuscule.
        const elision = [...ELISIONS].find((e) => lower.startsWith(e) && lower.length > e.length);
        if (elision) return elision + capitalizeWord(word.slice(elision.length));
      }
      return capitalizeWord(word);
    })
    .join(' ');
}

/** Champ prénom / nom d'une entrée : validé puis mis en forme. */
export function personNameSchema(opts: { min?: number; max: number }) {
  const base = z.string().max(opts.max);
  return (opts.min ? base.min(opts.min) : base).transform(normalizePersonName);
}
